
import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useClient } from "@/hooks/useClient";
import { LoadingState } from "@/components/error-handling/LoadingState";
import { ErrorState } from "@/components/error-handling/ErrorState";
import { useClientRealtime } from "@/hooks/useClientRealtime";
import { useClientDataPreview } from "@/hooks/useClientDataPreview";
import { useClientEnrichmentHandler } from "@/hooks/useClientEnrichmentHandler";
import { ClientDetailsContent } from "./client-details/ClientDetailsContent";
import { EnhancedClientDeletionDialog } from "./EnhancedClientDeletionDialog";
import { Client } from "./types";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

function isErrorWithMessage(error: unknown): error is { message: string } {
  return typeof error === 'object' && error !== null && 'message' in error && typeof (error as { message: unknown }).message === 'string';
}

export const ClientDetails = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [showEditForm, setShowEditForm] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  
  const { data: client, isLoading, error, refetch: refetchClient } = useClient(id);

  // Setup realtime updates for client data
  useClientRealtime({ clientId: id, currentClient: client, refetchClient });
  
  // Get fields with new data for highlighting - this returns a Set<keyof Client>
  const fieldsWithNewDataSet = useClientDataPreview(client);
  
  // Use the enhanced enrichment handler
  const {
    handleEnrichClient,
    isEnrichmentTriggering,
    isEnrichmentPolling,
    isEnrichmentDialogOpen,
    enrichmentDialogData,
    lastEnrichmentData,
    handleEnrichmentDialogClose,
    handleEnrichmentApplied,
    handleCancelEnrichment,
    isCancellingEnrichment,
  } = useClientEnrichmentHandler({ client, refetchClient });
  
  useEffect(() => {
    if (!id) {
      console.warn("No client ID provided in ClientDetails.");
    }
  }, [id]);

  if (isLoading) {
    return <LoadingState />;
  }

  if (error) {
    return (
      <ErrorState 
        title="Error loading client"
        description={error instanceof Error ? error.message : "Failed to load client details"}
        onRetry={() => refetchClient()}
      />
    );
  }

  if (!client) {
    return <ErrorState 
      title="Client not found" 
      description="The requested client could not be found." 
      onRetry={id ? () => refetchClient() : undefined} 
    />;
  }

  // Define callbacks for ClientDetailsContent
  const handleEdit = () => setShowEditForm(prev => !prev);
  const handleCloseEditForm = () => setShowEditForm(false);
  const handleSuccessEditForm = () => {
    setShowEditForm(false);
    refetchClient();
  };
  
  const handleDeleteClientTrigger = () => {
    if (!client) return;
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteClient = async () => {
    if (!client || !id) return;

    try {
      const { error: deleteError } = await supabase
        .from('clients')
        .delete()
        .eq('id', id);

      if (deleteError) {
        throw deleteError;
      }

      toast.success(`Client "${client.name}" and all associated data deleted successfully.`);
      navigate("/clients", { state: { refreshClients: true } });
    } catch (err: unknown) {
      console.error("Error deleting client:", err);
      const errorMessage = isErrorWithMessage(err) ? err.message : 'Unknown error';
      toast.error(`Failed to delete client: ${errorMessage}`);
      throw err; // Re-throw to let the dialog handle the error state
    }
  };

  return (
    <>
      <ClientDetailsContent
        client={client}
        showEditForm={showEditForm}
        onEdit={handleEdit}
        onDeleteClient={handleDeleteClientTrigger}
        onCloseEditForm={handleCloseEditForm}
        onSuccessEditForm={handleSuccessEditForm}
        refetchClient={refetchClient}
        fieldsWithNewDataSet={fieldsWithNewDataSet}
        handleEnrichClient={handleEnrichClient}
        isEnrichmentTriggering={isEnrichmentTriggering}
        isEnrichmentDialogOpen={isEnrichmentDialogOpen}
        enrichmentDialogData={enrichmentDialogData}
        handleEnrichmentDialogClose={handleEnrichmentDialogClose}
        handleEnrichmentApplied={handleEnrichmentApplied}
        isEnrichmentPolling={isEnrichmentPolling}
        lastEnrichmentData={lastEnrichmentData}
        handleCancelEnrichment={handleCancelEnrichment}
        isCancellingEnrichment={isCancellingEnrichment}
      />
      
      <EnhancedClientDeletionDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        onConfirm={confirmDeleteClient}
        client={client}
      />
    </>
  );
};

export default ClientDetails;
